// ABOUTME: Re-fetches a query while any of its localized texts is still PENDING.
// ABOUTME: Translations are produced in the background and land within a few seconds (WA-762).

import { useEffect, useEffectEvent } from "react"
import { hasPendingTranslation } from "@shared/ui/l10n"

/** The backend states a translation is typically ready in under 5 s. */
const RETRY_DELAY_MS = 4000
/** Stop after this many rounds so a stuck PENDING never becomes a polling loop. */
const MAX_ATTEMPTS = 3

/**
 * Retries a query a few seconds after it comes back with a PENDING translation,
 * up to MAX_ATTEMPTS times, until nothing is pending anymore.
 *
 * Call it once per query — not per card — so a month of workouts triggers one
 * refetch rather than one per item.
 */
export function useL10nPendingRefetch(data: unknown, refetch: (() => unknown) | undefined): void {
  const isPending = hasPendingTranslation(data)

  // Read through effect events inside the loop below. `isStillPending` lets a
  // round that is already in flight see the freshest state, and `refetchLatest`
  // is an effect event so that a caller whose callback identity churns per
  // render doesn't restart the loop and reset its attempt count.
  const isStillPending = useEffectEvent(() => isPending)
  const refetchLatest = useEffectEvent(() => refetch?.())

  useEffect(() => {
    if (!isPending) {
      return
    }

    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout>

    const run = async () => {
      attempts += 1
      try {
        await refetchLatest()
      } catch {
        // A failed round (flaky network) still counts, and the loop keeps its
        // own schedule instead of stalling on an unchanged result.
      }
      if (cancelled || attempts >= MAX_ATTEMPTS || !isStillPending()) {
        return
      }
      timer = setTimeout(() => void run(), RETRY_DELAY_MS)
    }

    timer = setTimeout(() => void run(), RETRY_DELAY_MS)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // `data` belongs in the deps precisely because Apollo reuses the reference
    // when a result is unchanged: PENDING→PENDING doesn't restart the effect, so
    // the self-scheduling loop above keeps counting its attempts. A genuinely
    // different result (new week, other athlete, part of the batch translated)
    // does restart it, giving the new data its own budget rather than making it
    // inherit the attempts the previous result used up.
  }, [isPending, data])
}
